/**
 * Gesprochenes Gespräch über WebRTC.
 *
 * Warum nicht die alte Kette: dort ging jeder Satz einzeln zum Server, die Antwort
 * wurde erst komplett heruntergeladen und dabei das Mikrofon abgeschaltet. Hier
 * fließt der Ton in beide Richtungen dauerhaft — deshalb hört man die Antwort ab
 * der ersten Silbe und kann dazwischenreden.
 *
 * Der Browser hält nur Audio. Anweisung, Werkzeugliste und Schlüssel bleiben im
 * Backend; wir schicken lediglich unser SDP-Angebot dorthin.
 */
import api from '@/lib/axios';

/** Nach so langer Stille wird die Sitzung beendet — Sprachminuten kosten Geld. */
export const IDLE_TIMEOUT_MS = 90000;

/**
 * Warum das Gespräch hier nicht geht — oder null, wenn es geht.
 *
 * Wichtig ist die Begründung, nicht das Ja/Nein: ein stummer Rückfall auf die
 * alte Kette sieht für den Bedienenden aus wie „nichts hat sich geändert“, und
 * genau das war der Ausgangspunkt der ganzen Beschwerde. Der häufigste Fall ist
 * kein defekter Browser, sondern eine unverschlüsselte Verbindung: ohne https
 * gibt es `navigator.mediaDevices` überhaupt nicht.
 */
export function realtimeBlocker() {
  if (typeof window === 'undefined') return 'Kein Browser.';
  if (!navigator?.mediaDevices?.getUserMedia) {
    if (window.isSecureContext === false) {
      return 'Mikrofon nur über https erreichbar — die Seite läuft unverschlüsselt.';
    }
    return 'Dieser Browser gibt kein Mikrofon frei (Chrome, Safari oder Edge nutzen).';
  }
  if (!window.RTCPeerConnection) {
    return 'Dieser Browser kann kein Sprachgespräch (WebRTC fehlt).';
  }
  return null;
}

export function isRealtimeSupported() {
  return realtimeBlocker() === null;
}

export async function fetchRealtimeStatus() {
  const { data } = await api.get('/voice/realtime/status');
  return data;
}

/**
 * Der Klartext aus einer Fehlerantwort.
 *
 * Die Antwort kommt als Rohtext, weil der Aufruf SDP überträgt — ohne das
 * Auspacken stand die JSON-Struktur samt Fluchtzeichen in der Sprachleiste und
 * verdeckte die eigentliche Aussage.
 */
export function errorDetail(err) {
  const raw = err?.response?.data;
  if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.detail) return String(parsed.detail).slice(0, 200);
    } catch { /* dann der Rohtext */ }
  }
  if (raw && typeof raw === 'object' && raw.detail) return String(raw.detail).slice(0, 200);
  return String(raw || err?.message || '').slice(0, 200);
}

/**
 * Baut die Sitzung auf und gibt eine Handhabe zum Beenden zurück.
 *
 * @param {Object} opts
 * @param {string} opts.pathname          aktuelle Seite, geht in die Anweisung
 * @param {(name:string, args:object)=>any} opts.runTool  Werkzeug ausführen; alles,
 *        was der Browser nicht selbst kann, geht von hier an das Backend
 * @param {(state:string)=>void} opts.onState   'connecting' | 'listening' | 'speaking' | 'closed'
 * @param {(text:string, final:boolean)=>void} opts.onUserText
 * @param {(text:string, final:boolean)=>void} opts.onAssistantText
 * @param {(message:string)=>void} opts.onError
 */
export async function startRealtimeSession({
  pathname = '',
  runTool,
  onState = () => {},
  onUserText = () => {},
  onAssistantText = () => {},
  onError = () => {},
  idleTimeoutMs = IDLE_TIMEOUT_MS,
} = {}) {
  if (!isRealtimeSupported()) {
    throw new Error('Dieser Browser kann kein Sprachgespräch (WebRTC fehlt).');
  }

  let closed = false;
  let pc = null;
  let mic = null;
  let audioEl = null;
  let channel = null;
  let idleTimer = null;

  const stop = () => {
    if (closed) return;
    closed = true;
    if (idleTimer) clearTimeout(idleTimer);
    try { channel?.close(); } catch { /* egal */ }
    try { mic?.getTracks().forEach((t) => t.stop()); } catch { /* egal */ }
    try { pc?.close(); } catch { /* egal */ }
    if (audioEl) {
      audioEl.srcObject = null;
      audioEl.remove();
      audioEl = null;
    }
    onState('closed');
  };

  const touchIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    if (!idleTimeoutMs) return;
    idleTimer = setTimeout(stop, idleTimeoutMs);
  };

  const send = (event) => {
    if (closed || channel?.readyState !== 'open') return;
    channel.send(JSON.stringify(event));
  };

  /** Ergebnis eines Werkzeugs zurück ins Gespräch und weitersprechen lassen. */
  const answerTool = async (callId, name, args) => {
    let output;
    try {
      output = await runTool?.(name, args);
    } catch (err) {
      output = { error: String(err?.message || err || 'Werkzeug fehlgeschlagen') };
    }
    send({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: JSON.stringify(output ?? { ok: true }),
      },
    });
    send({ type: 'response.create' });
  };

  const handleEvent = (event) => {
    switch (event.type) {
      case 'session.created':
      case 'session.updated':
        onState('listening');
        touchIdle();
        break;

      // Was die Person gesagt hat — nur für die Anzeige.
      case 'conversation.item.input_audio_transcription.delta':
        onUserText(event.delta || '', false);
        touchIdle();
        break;
      case 'conversation.item.input_audio_transcription.completed':
        onUserText(event.transcript || '', true);
        touchIdle();
        break;

      case 'input_audio_buffer.speech_started':
        // Dazwischenreden: OpenAI bricht die laufende Antwort selbst ab.
        onState('listening');
        touchIdle();
        break;

      case 'response.output_audio_transcript.delta':
        onAssistantText(event.delta || '', false);
        onState('speaking');
        break;
      case 'response.output_audio_transcript.done':
        onAssistantText(event.transcript || '', true);
        break;

      case 'response.done':
        onState('listening');
        touchIdle();
        break;

      case 'response.function_call_arguments.done': {
        let args = {};
        try { args = event.arguments ? JSON.parse(event.arguments) : {}; } catch { args = {}; }
        answerTool(event.call_id, event.name, args);
        touchIdle();
        break;
      }

      case 'error':
        onError(event.error?.message || 'Fehler in der Sprachsitzung');
        break;

      default:
        break;
    }
  };

  onState('connecting');
  try {
    mic = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    stop();
    throw new Error('Mikrofonzugriff fehlt — einmal im Browser erlauben.');
  }

  pc = new RTCPeerConnection();

  audioEl = document.createElement('audio');
  audioEl.autoplay = true;
  audioEl.style.display = 'none';
  document.body.appendChild(audioEl);
  pc.ontrack = (e) => { if (audioEl) audioEl.srcObject = e.streams[0]; };

  pc.addTrack(mic.getAudioTracks()[0], mic);

  channel = pc.createDataChannel('oai-events');
  channel.onmessage = (e) => {
    let event;
    try { event = JSON.parse(e.data); } catch { return; }
    handleEvent(event);
  };

  pc.onconnectionstatechange = () => {
    if (['failed', 'disconnected', 'closed'].includes(pc?.connectionState)) stop();
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  // Über die gemeinsame axios-Instanz, damit Sitzungscookie und Mandantenkopf
  // genauso mitgehen wie bei jedem anderen Aufruf.
  let res;
  try {
    res = await api.post('/voice/realtime/call', offer.sdp, {
      params: { pathname },
      headers: { 'Content-Type': 'application/sdp' },
      responseType: 'text',
      transformRequest: [(body) => body],
    });
  } catch (err) {
    stop();
    if (err?.response?.status === 503) {
      throw new Error('Kein OpenAI-Schlüssel hinterlegt — Einstellungen → KI.');
    }
    throw new Error(`Sprachsitzung abgelehnt (${err?.response?.status || '?'}). ${errorDetail(err)}`);
  }

  await pc.setRemoteDescription({ type: 'answer', sdp: String(res.data || '') });
  touchIdle();

  return {
    stop,
    /** Ohne Werkzeuge aufgebaut: das Modell kann dann nur reden, nichts abfragen. */
    limited: res.headers?.['x-voice-session'] === 'minimal',
    /** Text einwerfen, ohne zu sprechen — für Tastaturbedienung. */
    sendText: (text) => {
      const value = String(text || '').trim();
      if (!value) return;
      send({
        type: 'conversation.item.create',
        item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: value }] },
      });
      send({ type: 'response.create' });
    },
  };
}
